import axios from 'axios';
import axiosInstance from 'services/axiosConfig';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  AI_FASHION_STORE__BASE_PATH,
  LARGE_IMAGE_PATH,
  SMALL_IMAGE_PATH,
  TESTTASK_ID,
  TEST_IMAGE_ID,
} from 'utils/configs';

import { setUpdateStatusState } from '../../redux/user-data/userDataSlice';

import handleError from 'services/handleError';
import getTaskList from 'services/getTaskList';
import deleteTask from 'services/deleteTask';
import sendFeedback from 'services/sendFeedback';
import getCachedImageBlobUrl from 'services/getCachedImageBlobUrl';

import defaultInputImage from 'media/images/test_input.jpg';
import defaultResultImage from 'media/images/test_result.jpg';
import defaultPreviewImage from 'media/images/logo_orgn.svg';

const defaultTask = {
  id: TESTTASK_ID,
  name: 'Example',
  images: [
    {
      id: TEST_IMAGE_ID,
      location: defaultInputImage,
      location_basic: defaultInputImage,
      location_large: defaultInputImage,
      location_small: defaultInputImage,
      media_type: 'image',
      source: {
        type: 'test',
      },
    },
    {
      id: `${TEST_IMAGE_ID}2`,
      location: defaultResultImage,
      location_basic: defaultResultImage,
      location_large: defaultResultImage,
      location_small: defaultResultImage,
      media_type: 'image',
      source: {
        type: 'test',
      },
    },
  ],
  details: {
    subject: 'Example',
    description:
      "I'm a product description. I'm a great place to add more details about your product such as sizing, material, etc.",
    info: "I'm a product info. I'm a great place to add more details about your product such as care and cleaning instructions.",
  },
};

function getBaseUrl(id) {
  return `${AI_FASHION_STORE__BASE_PATH}/${id}/image/`;
}

const processImages = async dataArray => {
  return await Promise.all(
    dataArray.map(async item => {
      const imagesWithBlobs = await Promise.all(
        item.images.map(async img => ({
          ...img,

          location_basic: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            img.location
          ),
          location_large:
            img.media_type === 'video'
              ? await getCachedImageBlobUrl(getBaseUrl, item.id, img.location)
              : await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  img.location,
                  LARGE_IMAGE_PATH
                ),
          location_small:
            img.media_type === 'video'
              ? await getCachedImageBlobUrl(getBaseUrl, item.id, img.location)
              : await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  img.location,
                  SMALL_IMAGE_PATH
                ),
          ...(img.media_type === 'video' && img.preview_location
            ? {
                preview_location: await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  img.preview_location
                ),
                preview_location_small: await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  img.preview_location
                ),
                preview_location_large: await getCachedImageBlobUrl(
                  getBaseUrl,
                  item.id,
                  img.preview_location
                ),
              }
            : {
                preview_location: defaultPreviewImage,
                preview_location_small: defaultPreviewImage,
                preview_location_large: defaultPreviewImage,
              }),
        }))
      );

      return {
        ...item,
        images: imagesWithBlobs,
      };
    })
  );
};

export const createFSOrderWithTwoFileTypes = createAsyncThunk(
  'fashionStore/createWithTwoFileTypes',

  async (orderData, thunkAPI) => {
    try {
      let taskId = null;
      const formData = new FormData();

      if (orderData.ownImages.length > 0) {
        orderData.ownImages.forEach((item, index) => {
          formData.append('images', orderData.ownImages[index]);
        });

        const { data } = await axios.post(
          `${AI_FASHION_STORE__BASE_PATH}/create-from-uploaded`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        taskId = data;
      }

      if (
        orderData.galleryImages.length > 0 &&
        orderData.ownImages.length > 0
      ) {
        await axios.post(
          `${AI_FASHION_STORE__BASE_PATH}/${taskId.id}/image/add-from-gallery`,
          { images: orderData.galleryImages }
        );
      }

      if (
        orderData.galleryImages.length > 0 &&
        orderData.ownImages.length === 0
      ) {
        const { data } = await axios.post(
          `${AI_FASHION_STORE__BASE_PATH}/create-from-gallery`,
          { images: orderData.galleryImages }
        );

        taskId = data;
      }

      return taskId;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const getFSOrderList = createAsyncThunk(
  'fashionStore/getFSOrderList',

  async (_, thunkAPI) => {
    try {
      let processedData = [];
      const data = await getTaskList(AI_FASHION_STORE__BASE_PATH, thunkAPI);

      if (data.length > 0) {
        processedData = await processImages(data); // Processing all images
      } else {
        processedData = [defaultTask];
      }

      return processedData;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const deleteFSOrder = createAsyncThunk(
  'fashionStore/deleteFSOrder',

  async (orderId, thunkAPI) => {
    try {
      const data = deleteTask(AI_FASHION_STORE__BASE_PATH, orderId, thunkAPI);

      return data;
    } catch (error) {
      console.error('❌ Error deleting:', error);
    }
  }
);

export const getOrderDetails = createAsyncThunk(
  'fashionStore/getOrderDetails',

  async (orderId, thunkAPI) => {
    try {
      let processedData = [];
      if (orderId === TESTTASK_ID) {
        processedData = [defaultTask];
      } else {
        const { data } = await axios.get(
          `${AI_FASHION_STORE__BASE_PATH}/${orderId}/read`
        );

        processedData = await processImages([data]); // Processing all images
      }

      return processedData[0];
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const addImageFromGallery = createAsyncThunk(
  'fashionStore/addImageFromGallery',

  async (orderData, thunkAPI) => {
    try {
      let processedData = [];

      await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${orderData.orderId}/image/add-from-gallery`,
        { images: orderData.images }
      );

      const { data } = await axios.get(
        `${AI_FASHION_STORE__BASE_PATH}/${orderData.orderId}/read`
      );

      processedData = await processImages([data]); // Processing all images

      return processedData[0];
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const addImageFromUploaded = createAsyncThunk(
  'fashionStore/addImageFromUploaded',

  async (orderData, thunkAPI) => {
    try {
      let processedData = [];
      const formData = new FormData();

      orderData.images.forEach(item => {
        formData.append('images', item);
      });

      await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${orderData.orderId}/image/add-from-uploaded`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const { data } = await axios.get(
        `${AI_FASHION_STORE__BASE_PATH}/${orderData.orderId}/read`
      );

      processedData = await processImages([data]); // Processing all images

      return processedData[0];
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const deleteImageFS = createAsyncThunk(
  'fashionStore/deleteImageFS',

  async (imageData, thunkAPI) => {
    try {
      await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${imageData.orderId}/image/${imageData.imageId}/delete`
      );

      return imageData.imageId;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const updateFSOrderDetails = createAsyncThunk(
  'fashionStore/updateFSOrderDetails',

  async (orderData, thunkAPI) => {
    try {
      await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${orderData.orderId}/details/update`,
        orderData.newMeta
      );

      return orderData;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const generateFSOrderDetails = createAsyncThunk(
  'fashionStore/generateFSOrderDetails',

  async (reqParams, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    const state = getState();
    const currentCreditNumber = state.userdata.credits;

    try {
      const { data } = await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${reqParams.orderId}/details/generate`,
        reqParams.meta
      );

      if (data) {
        dispatch(
          setUpdateStatusState({
            creditsToCompare: currentCreditNumber,
            status: true,
          })
        );
      }

      return data;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const generateFSOrderPrice = createAsyncThunk(
  'fashionStore/generateFSOrderPrice',

  async (reqParams, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    const state = getState();
    const currentCreditNumber = state.userdata.credits;

    try {
      const { data } = await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${reqParams.orderId}/price/generate`,
        reqParams.meta
      );

      if (data) {
        dispatch(
          setUpdateStatusState({
            creditsToCompare: currentCreditNumber,
            status: true,
          })
        );
      }

      return data;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const updateFSOrderPrice = createAsyncThunk(
  'fashionStore/updateFSOrderPrice',

  async (reqParams, thunkAPI) => {
    try {
      await axios.post(
        `${AI_FASHION_STORE__BASE_PATH}/${reqParams.orderId}/price/update`,
        reqParams.newMeta
      );

      return reqParams.newMeta;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);

export const sendFSImageFeedback = createAsyncThunk(
  'fashionStore/imageFeedback',

  async (feedbackBody, thunkAPI) => {
    try {
      const { value, taskId } = feedbackBody;
      const toolPath = `${AI_FASHION_STORE__BASE_PATH}`;
      const imageId = null;

      const responce = await sendFeedback(
        value,
        toolPath,
        taskId,
        imageId,
        thunkAPI
      );

      return responce;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const publishOrderInShopify = createAsyncThunk(
  'fashionStore/publishOrderInShopify',

  async (dataObj, thunkAPI) => {
    try {
      const { data } = await axiosInstance.post(
        `${AI_FASHION_STORE__BASE_PATH}/${dataObj.orderId}/publish-to-shopify`,
        null
      );

      return data.product_id;
    } catch (error) {
      if (!error.response) {
        return thunkAPI.rejectWithValue({
          message:
            'Network error. Please check your internet connection or try again later.',
          errorType: error.code || 'network_error',
          details: error.message,
        });
      }

      const status = error.response?.status;
      const data = error.response?.data;

      const errorDetails = await handleError(status, data);

      return thunkAPI.rejectWithValue(errorDetails);
    }
  }
);
